import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Loader2, Loader, Settings, Users, FileText, BarChart3, Check, X, 
  Star, Trash2, Edit, Eye, EyeOff, Play, RefreshCw, Vote, AlertCircle, 
  CheckCircle, Clock, Ban, Home, Newspaper, Handshake, Image,
  Calendar, Trophy, Layers, ChevronRight, Menu, LogOut, Shield,
  Download, Search, Plus, AlertTriangle, Activity, TrendingUp,
  Type, LayoutGrid, ImageIcon, Video, HelpCircle, MousePointer,
  ListOrdered, Quote, GripVertical, ChevronUp, ChevronDown, Copy,
  ExternalLink, Link, Save, Upload
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MediaSelector } from '../components/MediaSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ========== SIDEBAR NAVIGATION ==========
const Sidebar = ({ activeModule, setActiveModule, user, onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  
  const modules = [
    { id: 'dashboard', label: 'Tableau de bord', icon: BarChart3 },
    { id: 'phases', label: 'Gestion des Phases', icon: Layers },
    { id: 'categories', label: 'Catégories', icon: Layers },
    { id: 'proposals', label: 'Propositions', icon: FileText },
    { id: 'finalists', label: 'Finalistes', icon: Star },
    { id: 'votes', label: 'Votes', icon: Vote },
    { id: 'results', label: 'Résultats', icon: Trophy },
    { id: 'news', label: 'Actualités', icon: Newspaper },
    { id: 'partners', label: 'Partenaires', icon: Handshake },
    { id: 'supporters', label: 'Soutiens étoiles', icon: Star },
    { id: 'gala', label: 'Le Gala', icon: Calendar },
    { id: 'pages', label: 'Pages CMS', icon: Home },
    { id: 'visibility', label: 'Visibilité des pages', icon: Eye },
    { id: 'media', label: 'Médiathèque', icon: Image },
    { id: 'users', label: 'Utilisateurs', icon: Users },
    { id: 'settings', label: 'Paramètres', icon: Settings },
    { id: 'logs', label: 'Journal d\'audit', icon: Activity },
  ];

  return (
    <aside className={`fixed left-0 top-0 h-screen bg-obsidian-light border-r border-white/10 transition-all z-40 ${collapsed ? 'w-16' : 'w-64'}`}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          {!collapsed && (
            <div>
              <h1 className="font-syne font-bold text-gold text-lg">Admin</h1>
              <p className="text-xs text-white/40">Stered an Noz</p>
            </div>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="p-2 hover:bg-white/5 text-white/50">
            <Menu className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {modules.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveModule(id)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all ${
                activeModule === id
                  ? 'bg-gold/10 text-gold border-r-2 border-gold'
                  : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span>{label}</span>}
            </button>
          ))}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-white/10">
          {!collapsed && (
            <div className="mb-3">
              <p className="text-sm text-white font-medium truncate">{user?.name}</p>
              <p className="text-xs text-white/40 flex items-center gap-1">
                <Shield className="w-3 h-3" />
                {user?.role || 'admin'}
              </p>
            </div>
          )}
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm transition-colors"
          >
            <LogOut className="w-4 h-4" />
            {!collapsed && 'Déconnexion'}
          </button>
        </div>
      </div>
    </aside>
  );
};

// ========== DASHBOARD MODULE ==========
const DashboardModule = ({ data, onRefresh }) => {
  if (!data) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  const { stats, phase, alerts, recent_proposals, recent_activity } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-syne text-2xl font-bold text-white">Tableau de bord</h2>
        <button onClick={onRefresh} className="p-2 border border-white/10 hover:border-gold/50 text-white/50 hover:text-gold">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Alerts */}
      {alerts?.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, idx) => (
            <div key={idx} className={`p-3 border flex items-center gap-3 ${
              alert.type === 'warning' ? 'border-orange-500/30 bg-orange-500/10 text-orange-300' : 'border-blue-500/30 bg-blue-500/10 text-blue-300'
            }`}>
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              {alert.message}
            </div>
          ))}
        </div>
      )}

      {/* Phase Status */}
      <div className="p-4 border border-gold/30 bg-gold/5">
        <h3 className="font-syne font-bold text-white mb-2">Phase actuelle</h3>
        <div className="flex items-center gap-4">
          <span className={`px-3 py-1 text-sm font-bold ${phase?.phase1_open ? 'bg-green-500 text-white' : 'bg-white/10 text-white/50'}`}>
            Phase 1 {phase?.phase1_open ? '(Ouvert)' : '(Fermé)'}
          </span>
          <span className={`px-3 py-1 text-sm font-bold ${phase?.phase2_open ? 'bg-electric-blue text-white' : 'bg-white/10 text-white/50'}`}>
            Phase 2 {phase?.phase2_open ? '(Ouvert)' : '(Fermé)'}
          </span>
          <span className={`px-3 py-1 text-sm font-bold ${phase?.phase3_open ? 'bg-gold text-obsidian' : 'bg-white/10 text-white/50'}`}>
            Phase 3 {phase?.phase3_open ? '(Ouvert)' : '(Fermé)'}
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total propositions" value={stats?.total_proposals || 0} icon={FileText} />
        <StatCard label="En attente" value={stats?.pending_proposals || 0} icon={Clock} color="orange" />
        <StatCard label="Approuvées" value={stats?.approved_proposals || 0} icon={CheckCircle} color="green" />
        <StatCard label="Finalistes" value={stats?.finalists_count || 0} icon={Star} color="gold" />
        <StatCard label="Total votes" value={stats?.total_votes || 0} icon={Vote} color="cyan" />
        <StatCard label="Utilisateurs" value={stats?.total_users || 0} icon={Users} />
        <StatCard label="Partenaires" value={stats?.total_partners || 0} icon={Handshake} />
        <StatCard label="Actualités" value={stats?.total_news || 0} icon={Newspaper} />
      </div>

      {/* Recent Activity */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-white/10 p-4">
          <h3 className="font-syne font-bold text-white mb-4">Dernières propositions</h3>
          <div className="space-y-2">
            {recent_proposals?.slice(0, 5).map(p => (
              <div key={p.id} className="flex items-center justify-between text-sm p-2 bg-obsidian">
                <span className="text-white truncate">{p.nominee_name}</span>
                <span className={`text-xs px-2 py-0.5 ${
                  p.status === 'pending' ? 'bg-orange-500/20 text-orange-400' :
                  p.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                  'bg-red-500/20 text-red-400'
                }`}>{p.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-white/10 p-4">
          <h3 className="font-syne font-bold text-white mb-4">Activité récente</h3>
          <div className="space-y-2">
            {recent_activity?.slice(0, 5).map(log => (
              <div key={log.id} className="text-sm p-2 bg-obsidian">
                <span className="text-gold">{log.action}</span>
                <span className="text-white/50 ml-2">par {log.user_email}</span>
                <p className="text-xs text-white/30">{new Date(log.created_at).toLocaleString('fr-FR')}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color = 'white' }) => {
  const colors = {
    white: 'text-white',
    gold: 'text-gold',
    green: 'text-green-400',
    orange: 'text-orange-400',
    cyan: 'text-electric-cyan',
    red: 'text-red-400'
  };
  
  return (
    <div className="p-4 border border-white/10 bg-obsidian-light">
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${colors[color]}`} />
        <div>
          <div className={`text-2xl font-syne font-bold ${colors[color]}`}>{value}</div>
          <div className="text-xs text-white/50">{label}</div>
        </div>
      </div>
    </div>
  );
};

// ========== PHASES MODULE ==========
const PhasesModule = ({ phase, onUpdate }) => {
  const [localPhase, setLocalPhase] = useState(phase || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (phase) setLocalPhase(phase);
  }, [phase]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/phase`, localPhase);
      toast.success('Phases mises à jour');
      onUpdate();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const togglePhase = (phaseKey) => {
    const newPhase = { ...localPhase };
    newPhase[phaseKey] = !newPhase[phaseKey];
    
    // Auto-close other phases
    if (phaseKey === 'phase1_open' && newPhase.phase1_open) {
      newPhase.phase2_open = false;
      newPhase.phase3_open = false;
      newPhase.current_phase = 1;
    } else if (phaseKey === 'phase2_open' && newPhase.phase2_open) {
      newPhase.phase1_open = false;
      newPhase.phase3_open = false;
      newPhase.current_phase = 2;
    } else if (phaseKey === 'phase3_open' && newPhase.phase3_open) {
      newPhase.phase1_open = false;
      newPhase.phase2_open = false;
      newPhase.current_phase = 3;
    }
    
    setLocalPhase(newPhase);
  };

  return (
    <div className="space-y-6">
      <h2 className="font-syne text-2xl font-bold text-white">Gestion des Phases</h2>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Phase 1 */}
        <div className={`p-6 border ${localPhase.phase1_open ? 'border-green-500/50 bg-green-500/5' : 'border-white/10'}`}>
          <h3 className="font-syne text-xl font-bold text-white mb-2">Phase 1</h3>
          <p className="text-sm text-white/60 mb-4">Propositions publiques</p>
          <button
            onClick={() => togglePhase('phase1_open')}
            className={`w-full py-3 font-bold text-sm ${
              localPhase.phase1_open ? 'bg-green-500 text-white' : 'border border-white/20 text-white/70 hover:border-green-500/50'
            }`}
          >
            {localPhase.phase1_open ? 'OUVERT' : 'FERMÉ'}
          </button>
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-xs text-white/50">Date début</label>
              <input
                type="datetime-local"
                value={localPhase.phase1_start?.slice(0, 16) || ''}
                onChange={e => setLocalPhase({...localPhase, phase1_start: e.target.value})}
                className="w-full mt-1 px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-white/50">Date fin</label>
              <input
                type="datetime-local"
                value={localPhase.phase1_end?.slice(0, 16) || ''}
                onChange={e => setLocalPhase({...localPhase, phase1_end: e.target.value})}
                className="w-full mt-1 px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-white/50">Message public</label>
              <textarea
                value={localPhase.phase1_message || ''}
                onChange={e => setLocalPhase({...localPhase, phase1_message: e.target.value})}
                className="w-full mt-1 px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Phase 2 */}
        <div className={`p-6 border ${localPhase.phase2_open ? 'border-electric-blue/50 bg-electric-blue/5' : 'border-white/10'}`}>
          <h3 className="font-syne text-xl font-bold text-white mb-2">Phase 2</h3>
          <p className="text-sm text-white/60 mb-4">Vote final</p>
          <button
            onClick={() => togglePhase('phase2_open')}
            className={`w-full py-3 font-bold text-sm ${
              localPhase.phase2_open ? 'bg-electric-blue text-white' : 'border border-white/20 text-white/70 hover:border-electric-blue/50'
            }`}
          >
            {localPhase.phase2_open ? 'OUVERT' : 'FERMÉ'}
          </button>
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-xs text-white/50">Date début</label>
              <input
                type="datetime-local"
                value={localPhase.phase2_start?.slice(0, 16) || ''}
                onChange={e => setLocalPhase({...localPhase, phase2_start: e.target.value})}
                className="w-full mt-1 px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-white/50">Date fin</label>
              <input
                type="datetime-local"
                value={localPhase.phase2_end?.slice(0, 16) || ''}
                onChange={e => setLocalPhase({...localPhase, phase2_end: e.target.value})}
                className="w-full mt-1 px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-white/50">Message public</label>
              <textarea
                value={localPhase.phase2_message || ''}
                onChange={e => setLocalPhase({...localPhase, phase2_message: e.target.value})}
                className="w-full mt-1 px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Phase 3 */}
        <div className={`p-6 border ${localPhase.phase3_open ? 'border-gold/50 bg-gold/5' : 'border-white/10'}`}>
          <h3 className="font-syne text-xl font-bold text-white mb-2">Phase 3</h3>
          <p className="text-sm text-white/60 mb-4">Résultats / Gala</p>
          <button
            onClick={() => togglePhase('phase3_open')}
            className={`w-full py-3 font-bold text-sm ${
              localPhase.phase3_open ? 'bg-gold text-obsidian' : 'border border-white/20 text-white/70 hover:border-gold/50'
            }`}
          >
            {localPhase.phase3_open ? 'OUVERT' : 'FERMÉ'}
          </button>
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-xs text-white/50">Date gala</label>
              <input
                type="datetime-local"
                value={localPhase.phase3_start?.slice(0, 16) || ''}
                onChange={e => setLocalPhase({...localPhase, phase3_start: e.target.value})}
                className="w-full mt-1 px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-white/50">Message public</label>
              <textarea
                value={localPhase.phase3_message || ''}
                onChange={e => setLocalPhase({...localPhase, phase3_message: e.target.value})}
                className="w-full mt-1 px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
                rows={2}
              />
            </div>
            <div>
              <label className="text-xs text-white/50">Message fermé</label>
              <textarea
                value={localPhase.closed_message || ''}
                onChange={e => setLocalPhase({...localPhase, closed_message: e.target.value})}
                className="w-full mt-1 px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
                rows={2}
              />
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="bg-gold hover:bg-gold-glow text-obsidian font-bold px-6 py-3 flex items-center gap-2"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Enregistrer les modifications
      </button>
    </div>
  );
};

// ========== CATEGORIES MODULE ==========
const CategoriesModule = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories?include_hidden=true`);
      setCategories(res.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  const handleSave = async (category) => {
    try {
      if (category.id && categories.find(c => c.id === category.id)) {
        await axios.put(`${API}/admin/categories/${category.id}`, category);
        toast.success('Catégorie mise à jour');
      } else {
        await axios.post(`${API}/admin/categories`, category);
        toast.success('Catégorie créée');
      }
      setEditing(null);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await axios.delete(`${API}/admin/categories/${id}`);
      toast.success('Catégorie supprimée');
      fetchCategories();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-syne text-2xl font-bold text-white">Catégories</h2>
        <button
          onClick={() => setEditing({ name: '', slug: '', description: '', is_visible: true, order: categories.length + 1 })}
          className="bg-gold text-obsidian px-4 py-2 flex items-center gap-2 font-bold text-sm"
        >
          <Plus className="w-4 h-4" /> Nouvelle catégorie
        </button>
      </div>

      <div className="space-y-3">
        {categories.map(cat => (
          <div key={cat.id} className="p-4 border border-white/10 bg-obsidian-light flex items-center justify-between hover:border-gold/30 transition-colors">
            <div className="flex items-center gap-4 flex-1">
              <span className="text-white/30 w-6">{cat.order}</span>
              <div className="flex-1">
                <h3 className="font-bold text-white">{cat.name}</h3>
                <p className="text-sm text-white/50">{cat.name_breton}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  try {
                    await axios.put(`${API}/admin/categories/${cat.id}`, { ...cat, is_visible: !cat.is_visible });
                    toast.success(cat.is_visible ? 'Catégorie masquée' : 'Catégorie publiée');
                    fetchCategories();
                  } catch (error) {
                    toast.error('Erreur');
                  }
                }}
                className={`p-2 transition-colors ${
                  cat.is_visible
                    ? 'text-gold hover:bg-gold/10'
                    : 'text-white/30 hover:bg-white/5'
                }`}
                title={cat.is_visible ? 'Masquer cette catégorie' : 'Publier cette catégorie'}
              >
                <Eye className="w-4 h-4" />
              </button>
              <button onClick={() => setEditing(cat)} className="p-2 hover:bg-white/5 text-white/50 hover:text-white transition-colors" title="Modifier">
                <Edit className="w-4 h-4" />
              </button>
              <button onClick={() => handleDelete(cat.id)} className="p-2 hover:bg-red-500/10 text-red-400 transition-colors" title="Supprimer">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editing && (
        <CategoryEditModal category={editing} onSave={handleSave} onClose={() => setEditing(null)} />
      )}
    </div>
  );
};

const CategoryEditModal = ({ category, onSave, onClose }) => {
  const [form, setForm] = useState(category);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-obsidian/90" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-obsidian-light border border-white/10 p-6" onClick={e => e.stopPropagation()}>
        <h3 className="font-syne text-xl font-bold text-white mb-6">
          {category.id ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-white/50 mb-1 block">Nom</label>
            <input type="text" value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Nom breton</label>
            <input type="text" value={form.name_breton || ''} onChange={e => setForm({...form, name_breton: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Slug</label>
            <input type="text" value={form.slug || ''} onChange={e => setForm({...form, slug: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Ordre</label>
            <input type="number" value={form.order || 0} onChange={e => setForm({...form, order: parseInt(e.target.value)})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-white/50 mb-1 block">Description</label>
            <textarea value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" rows={2} />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-white/50 mb-1 block">Description longue</label>
            <textarea value={form.description_long || ''} onChange={e => setForm({...form, description_long: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" rows={3} />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-white/50 mb-1 block">Image URL</label>
            <input type="text" value={form.image_url || ''} onChange={e => setForm({...form, image_url: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div className="md:col-span-2">
            <div className="flex items-center justify-between p-3 bg-obsidian border border-white/10 rounded">
              <label className="text-sm font-semibold text-white">Statut de publication</label>
              <button
                type="button"
                onClick={() => setForm({...form, is_visible: !form.is_visible})}
                className={`px-4 py-2 rounded font-semibold text-sm transition-all ${
                  form.is_visible !== false
                    ? 'bg-gold/20 text-gold border border-gold/30'
                    : 'bg-white/10 text-white/50 border border-white/20'
                }`}
              >
                {form.is_visible !== false ? '✓ Publié (visible au public)' : '✗ Masqué (non visible)'}
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 border border-white/20 text-white hover:bg-white/5">Annuler</button>
          <button onClick={() => onSave(form)} className="px-4 py-2 bg-gold text-obsidian font-bold">Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

// ========== PROPOSALS MODULE ==========
const ProposalsModule = () => {
  const [proposals, setProposals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchData = async () => {
    try {
      const [propRes, catRes] = await Promise.all([
        axios.get(`${API}/proposals/admin/all`),
        axios.get(`${API}/categories`)
      ]);
      setProposals(propRes.data);
      setCategories(catRes.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleAction = async (id, action) => {
    setActionLoading(id);
    try {
      await axios.post(`${API}/admin/proposals/${id}/${action}`);
      toast.success(action === 'approve' ? 'Approuvé' : action === 'reject' ? 'Refusé' : 'Mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette proposition ?')) return;
    try {
      await axios.delete(`${API}/admin/proposals/${id}`);
      toast.success('Supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      await axios.put(`${API}/admin/proposals/${id}`, data);
      toast.success('Mis à jour');
      setEditing(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleAutoSelect = async () => {
    if (!window.confirm('Sélectionner automatiquement les Top 4 comme finalistes ?')) return;
    try {
      const res = await axios.post(`${API}/admin/proposals/auto-select-finalists`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const filtered = proposals.filter(p => {
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    if (filterCategory !== 'all' && p.category_id !== filterCategory) return false;
    if (search && !p.nominee_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || id;

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-syne text-2xl font-bold text-white">Propositions</h2>
        <button onClick={handleAutoSelect} className="bg-gold/10 border border-gold/50 text-gold px-4 py-2 text-sm font-bold hover:bg-gold/20">
          Auto-sélection Top 4
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="text"
            placeholder="Rechercher..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 pr-4 py-2 bg-obsidian border border-white/10 text-white text-sm w-48"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 bg-obsidian border-white/10 text-white text-sm">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-obsidian-light border-white/10 z-50">
            <SelectItem value="all" className="text-white">Tous</SelectItem>
            <SelectItem value="pending" className="text-orange-400">En attente</SelectItem>
            <SelectItem value="approved" className="text-green-400">Approuvées</SelectItem>
            <SelectItem value="rejected" className="text-red-400">Refusées</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-44 bg-obsidian border-white/10 text-white text-sm">
            <SelectValue placeholder="Catégorie" />
          </SelectTrigger>
          <SelectContent className="bg-obsidian-light border-white/10 z-50">
            <SelectItem value="all" className="text-white">Toutes</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.id} className="text-white">{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-white/40 self-center">{filtered.length} résultats</span>
      </div>

      {/* List */}
      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {filtered.map(p => (
          <div key={p.id} className={`p-4 border bg-obsidian-light ${p.is_finalist ? 'border-gold/50' : 'border-white/10'}`}>
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              {p.video_thumbnail && (
                <img src={p.video_thumbnail} alt="" className="w-20 h-14 object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-bold text-white">{p.nominee_name}</span>
                  <StatusBadge status={p.status} />
                  <span className="text-xs px-2 py-0.5 bg-white/5 text-white/50">{getCategoryName(p.category_id)}</span>
                  {p.is_finalist && <span className="text-xs px-2 py-0.5 bg-gold/20 text-gold">Finaliste</span>}
                </div>
                <p className="text-sm text-white/50 line-clamp-1">{p.description}</p>
                <p className="text-xs text-white/30 mt-1">{p.proposal_count} prop. • {p.vote_count || 0} votes • {p.proposer_name}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {p.video_url && (
                  <a href={p.video_url} target="_blank" rel="noopener noreferrer" className="p-2 text-white/50 hover:text-gold">
                    <Play className="w-4 h-4" />
                  </a>
                )}
                {p.status === 'pending' && (
                  <>
                    <button onClick={() => handleAction(p.id, 'approve')} disabled={actionLoading === p.id}
                      className="p-2 bg-green-500/20 hover:bg-green-500/40 text-green-400">
                      {actionLoading === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </button>
                    <button onClick={() => handleAction(p.id, 'reject')} className="p-2 bg-red-500/20 hover:bg-red-500/40 text-red-400">
                      <X className="w-4 h-4" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => handleAction(p.id, `finalist?is_finalist=${!p.is_finalist}`)}
                  className={`p-2 ${p.is_finalist ? 'bg-gold/20 text-gold' : 'bg-white/5 text-white/50 hover:text-gold'}`}
                >
                  <Star className="w-4 h-4" />
                </button>
                <button onClick={() => setEditing(p)} className="p-2 bg-white/5 hover:bg-white/10 text-white/50 hover:text-white">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(p.id)} className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {editing && (
        <ProposalEditModal proposal={editing} categories={categories} onSave={(data) => handleUpdate(editing.id, data)} onClose={() => setEditing(null)} />
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    approved: 'bg-green-500/20 text-green-400 border-green-500/30',
    rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
    duplicate: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };
  const icons = { pending: Clock, approved: CheckCircle, rejected: Ban };
  const Icon = icons[status] || Clock;
  
  return (
    <span className={`text-xs px-2 py-0.5 border ${styles[status] || styles.pending}`}>
      <Icon className="w-3 h-3 inline mr-1" />{status}
    </span>
  );
};

const ProposalEditModal = ({ proposal, categories, onSave, onClose }) => {
  const [form, setForm] = useState(proposal);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-obsidian/90" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-obsidian-light border border-white/10 p-6" onClick={e => e.stopPropagation()}>
        <h3 className="font-syne text-xl font-bold text-white mb-6">Modifier la proposition</h3>
        
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/50 mb-1 block">Nom</label>
              <input type="text" value={form.nominee_name || ''} onChange={e => setForm({...form, nominee_name: e.target.value})}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
            </div>
            <div>
              <label className="text-sm text-white/50 mb-1 block">Catégorie</label>
              <Select value={form.category_id} onValueChange={v => setForm({...form, category_id: v})}>
                <SelectTrigger className="w-full bg-obsidian border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-obsidian-light border-white/10 z-50">
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id} className="text-white">{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Description</label>
            <textarea value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" rows={3} />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">URL Vidéo</label>
            <input type="url" value={form.video_url || ''} onChange={e => setForm({...form, video_url: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Bio finaliste</label>
            <textarea value={form.finalist_bio || ''} onChange={e => setForm({...form, finalist_bio: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" rows={2} />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Notes admin</label>
            <textarea value={form.admin_notes || ''} onChange={e => setForm({...form, admin_notes: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" rows={2} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/50 mb-1 block">Statut</label>
              <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger className="w-full bg-obsidian border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-obsidian-light border-white/10 z-50">
                  <SelectItem value="pending" className="text-orange-400">En attente</SelectItem>
                  <SelectItem value="approved" className="text-green-400">Approuvé</SelectItem>
                  <SelectItem value="rejected" className="text-red-400">Refusé</SelectItem>
                  <SelectItem value="duplicate" className="text-gray-400">Doublon</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-white/50 mb-1 block">Ordre finaliste</label>
              <input type="number" value={form.finalist_order || 0} onChange={e => setForm({...form, finalist_order: parseInt(e.target.value)})}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 border border-white/20 text-white hover:bg-white/5">Annuler</button>
          <button onClick={() => onSave(form)} className="px-4 py-2 bg-gold text-obsidian font-bold">Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

// ========== VOTES MODULE ==========
const VotesModule = () => {
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [statsRes, catRes, propRes, anomRes] = await Promise.all([
        axios.get(`${API}/admin/votes/stats`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/proposals?finalists_only=true`),
        axios.get(`${API}/admin/votes/anomalies`).catch(() => ({ data: { anomalies: [] } }))
      ]);
      setStats(statsRes.data);
      setCategories(catRes.data);
      setProposals(propRes.data);
      setAnomalies(anomRes.data.anomalies || []);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/admin/votes/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'votes_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur export');
    }
  };

  const getProposalName = (id) => proposals.find(p => p.id === id)?.nominee_name || id;

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-syne text-2xl font-bold text-white">Votes</h2>
        <button onClick={handleExport} className="bg-white/5 border border-white/10 text-white px-4 py-2 flex items-center gap-2 text-sm hover:bg-white/10">
          <Download className="w-4 h-4" /> Exporter CSV
        </button>
      </div>

      <div className="p-4 border border-gold/30 bg-gold/5">
        <div className="text-3xl font-syne font-bold text-gold">{stats?.total || 0}</div>
        <div className="text-white/50">Votes totaux</div>
      </div>

      {/* Anomalies */}
      {anomalies.length > 0 && (
        <div className="p-4 border border-red-500/30 bg-red-500/10">
          <h3 className="font-bold text-red-400 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Anomalies détectées
          </h3>
          <p className="text-sm text-red-300">{anomalies.length} IP avec plus de 10 votes</p>
        </div>
      )}

      {/* By category */}
      <div className="space-y-4">
        {categories.map(cat => {
          const catVotes = stats?.by_category?.[cat.id] || {};
          const total = Object.values(catVotes).reduce((a, b) => a + b, 0);
          
          return (
            <div key={cat.id} className="p-4 border border-white/10 bg-obsidian-light">
              <div className="flex items-center justify-between mb-3">
                <span className="font-bold text-white">{cat.name}</span>
                <span className="text-gold">{total} votes</span>
              </div>
              {Object.keys(catVotes).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(catVotes).sort((a, b) => b[1] - a[1]).map(([propId, count]) => (
                    <div key={propId} className="flex items-center justify-between text-sm">
                      <span className="text-white/60">{getProposalName(propId)}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full bg-gold" style={{ width: `${(count / total) * 100}%` }} />
                        </div>
                        <span className="text-white/40 w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-white/30">Aucun vote</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ========== NEWS MODULE ==========
const NewsModule = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchNews = async () => {
    try {
      const res = await axios.get(`${API}/admin/news`);
      setNews(res.data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNews(); }, []);

  const handleSave = async (article) => {
    try {
      if (article.id) {
        await axios.put(`${API}/admin/news/${article.id}`, article);
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/admin/news`, article);
        toast.success('Article créé');
      }
      setEditing(null);
      fetchNews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await axios.delete(`${API}/admin/news/${id}`);
      toast.success('Supprimé');
      fetchNews();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-syne text-2xl font-bold text-white">Actualités</h2>
        <button
          onClick={() => setEditing({ title: '', slug: '', summary: '', content: '', cover_image: '', link_url: '', video_url: '', status: 'draft' })}
          className="bg-gold text-obsidian px-4 py-2 flex items-center gap-2 font-bold text-sm"
        >
          <Plus className="w-4 h-4" /> Nouvel article
        </button>
      </div>

      <div className="space-y-3">
        {news.map(article => (
          <div key={article.id} className="p-4 border border-white/10 bg-obsidian-light flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-white">{article.title}</span>
                <span className={`text-xs px-2 py-0.5 ${
                  article.status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'
                }`}>{article.status}</span>
                {article.is_featured && <span className="text-xs px-2 py-0.5 bg-gold/20 text-gold">À la une</span>}
              </div>
              <p className="text-sm text-white/50 line-clamp-1">{article.summary}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(article)} className="p-2 hover:bg-white/5 text-white/50 hover:text-white">
                <Edit className="w-4 h-4" />
              </button>
              <button onClick={() => handleDelete(article.id)} className="p-2 hover:bg-red-500/10 text-red-400">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {news.length === 0 && <p className="text-center text-white/40 py-8">Aucun article</p>}
      </div>

      {editing && (
        <NewsEditModal article={editing} onSave={handleSave} onClose={() => setEditing(null)} />
      )}
    </div>
  );
};

const NewsEditModal = ({ article, onSave, onClose }) => {
  const [form, setForm] = useState(article);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);

  const handleSelectMedia = (mediaItem) => {
    setForm({...form, cover_image: mediaItem.url});
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-obsidian/90" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-obsidian-light border border-white/10 p-6" onClick={e => e.stopPropagation()}>
        <h3 className="font-syne text-xl font-bold text-white mb-6">
          {article.id ? 'Modifier l\'article' : 'Nouvel article'}
        </h3>
        
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/50 mb-1 block">Titre</label>
              <input type="text" value={form.title || ''} onChange={e => setForm({...form, title: e.target.value})}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
            </div>
            <div>
              <label className="text-sm text-white/50 mb-1 block">Slug</label>
              <input type="text" value={form.slug || ''} onChange={e => setForm({...form, slug: e.target.value})}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
            </div>
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Résumé</label>
            <textarea value={form.summary || ''} onChange={e => setForm({...form, summary: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" rows={2} />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Contenu</label>
            <textarea value={form.content || ''} onChange={e => setForm({...form, content: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" rows={8} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/50 mb-1 block">Image de couverture</label>
              <div className="space-y-2">
                {form.cover_image && (
                  <div className="relative w-full h-32 bg-obsidian border border-white/10 rounded overflow-hidden">
                    <img 
                      src={form.cover_image} 
                      alt="cover" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => setForm({...form, cover_image: ''})}
                      className="absolute top-1 right-1 p-1 bg-red-500 rounded hover:bg-red-600"
                    >
                      <X size={14} className="text-white" />
                    </button>
                  </div>
                )}
                <button
                  onClick={() => setMediaDialogOpen(true)}
                  className="w-full px-3 py-2 bg-obsidian border border-white/20 text-white hover:bg-white/5 flex items-center justify-center gap-2"
                >
                  <Image size={16} />
                  Choisir dans la médiathèque
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm text-white/50 mb-1 block">Lien de l'article (URL)</label>
              <input type="text" value={form.link_url || ''} onChange={e => setForm({...form, link_url: e.target.value})}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/50 mb-1 block">Vidéo (URL)</label>
              <input type="text" value={form.video_url || ''} onChange={e => setForm({...form, video_url: e.target.value})}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/50 mb-1 block">Statut</label>
              <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger className="w-full bg-obsidian border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-obsidian-light border-white/10 z-50">
                  <SelectItem value="draft" className="text-orange-400">Brouillon</SelectItem>
                  <SelectItem value="published" className="text-green-400">Publié</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input type="checkbox" checked={form.is_featured} onChange={e => setForm({...form, is_featured: e.target.checked})} />
              <label className="text-sm text-white/70">À la une</label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 border border-white/20 text-white hover:bg-white/5">Annuler</button>
          <button onClick={() => onSave(form)} className="px-4 py-2 bg-gold text-obsidian font-bold">Enregistrer</button>
        </div>

        <MediaSelector
          isOpen={mediaDialogOpen}
          onClose={() => setMediaDialogOpen(false)}
          onSelect={handleSelectMedia}
          currentUrl={form.cover_image}
        />
      </div>
    </div>
  );
};

// ========== PARTNERS MODULE ==========
const PartnersModule = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API}/admin/partners`);
      setPartners(res.data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPartners(); }, []);

  const handleSave = async (partner) => {
    try {
      if (partner.id) {
        await axios.put(`${API}/admin/partners/${partner.id}`, partner);
        toast.success('Partenaire mis à jour');
      } else {
        await axios.post(`${API}/admin/partners`, partner);
        toast.success('Partenaire créé');
      }
      setEditing(null);
      fetchPartners();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    try {
      await axios.delete(`${API}/admin/partners/${id}`);
      toast.success('Supprimé');
      fetchPartners();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-syne text-2xl font-bold text-white">Partenaires</h2>
        <button
          onClick={() => setEditing({ name: '', type: 'entreprise', is_visible: true, order: partners.length + 1, display_locations: ['partners'] })}
          className="bg-gold text-obsidian px-4 py-2 flex items-center gap-2 font-bold text-sm"
        >
          <Plus className="w-4 h-4" /> Nouveau partenaire
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {partners.map(p => (
          <div key={p.id} className="p-4 border border-white/10 bg-obsidian-light">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-bold text-white">{p.name}</h3>
                <p className="text-xs text-white/50">{p.type}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(p)} className="p-1 text-white/50 hover:text-white">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(p.id)} className="p-1 text-red-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            {p.logo_url && <img src={p.logo_url} alt={p.name} className="w-full h-16 object-contain bg-white/5 mb-2" />}
            <p className="text-sm text-white/50 line-clamp-2">{p.description}</p>
          </div>
        ))}
      </div>

      {editing && (
        <PartnerEditModal partner={editing} onSave={handleSave} onClose={() => setEditing(null)} />
      )}
    </div>
  );
};

const PartnerEditModal = ({ partner, onSave, onClose }) => {
  const [form, setForm] = useState(partner);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-obsidian/90" onClick={onClose}>
      <div className="w-full max-w-lg bg-obsidian-light border border-white/10 p-6" onClick={e => e.stopPropagation()}>
        <h3 className="font-syne text-xl font-bold text-white mb-6">
          {partner.id ? 'Modifier le partenaire' : 'Nouveau partenaire'}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-white/50 mb-1 block">Nom</label>
            <input type="text" value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Type</label>
            <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
              <SelectTrigger className="w-full bg-obsidian border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-obsidian-light border-white/10 z-50">
                <SelectItem value="institution" className="text-white">Institution</SelectItem>
                <SelectItem value="entreprise" className="text-white">Entreprise</SelectItem>
                <SelectItem value="media" className="text-white">Média</SelectItem>
                <SelectItem value="culturel" className="text-white">Partenaire culturel</SelectItem>
                <SelectItem value="sponsor" className="text-white">Sponsor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Logo (URL)</label>
            <input type="text" value={form.logo_url || ''} onChange={e => setForm({...form, logo_url: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Description</label>
            <textarea value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" rows={2} />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Site web</label>
            <input type="text" value={form.website_url || ''} onChange={e => setForm({...form, website_url: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/50 mb-1 block">Ordre</label>
              <input type="number" value={form.order || 0} onChange={e => setForm({...form, order: parseInt(e.target.value)})}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
            </div>
            <div>
              <label className="text-sm text-white/50 mb-1 block">Niveau</label>
              <Select value={form.level || 'standard'} onValueChange={v => setForm({...form, level: v})}>
                <SelectTrigger className="w-full bg-obsidian border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-obsidian-light border-white/10 z-50">
                  <SelectItem value="premium" className="text-gold">Premium</SelectItem>
                  <SelectItem value="gold" className="text-yellow-400">Gold</SelectItem>
                  <SelectItem value="standard" className="text-white">Standard</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.is_visible !== false} onChange={e => setForm({...form, is_visible: e.target.checked})} />
            <label className="text-sm text-white/70">Visible</label>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 border border-white/20 text-white hover:bg-white/5">Annuler</button>
          <button onClick={() => onSave(form)} className="px-4 py-2 bg-gold text-obsidian font-bold">Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

// ========== SETTINGS MODULE ==========
const SettingsModule = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings);
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      <h2 className="font-syne text-2xl font-bold text-white">Paramètres généraux</h2>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4 p-6 border border-white/10 bg-obsidian-light">
          <h3 className="font-syne font-bold text-gold mb-4">Identité</h3>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Nom du site</label>
            <input type="text" value={settings?.site_name || ''} onChange={e => setSettings({...settings, site_name: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Logo URL</label>
            <input type="text" value={settings?.logo_url || ''} onChange={e => setSettings({...settings, logo_url: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Email contact</label>
            <input type="email" value={settings?.contact_email || ''} onChange={e => setSettings({...settings, contact_email: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Édition en cours</label>
            <input type="text" value={settings?.current_edition || ''} onChange={e => setSettings({...settings, current_edition: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
        </div>

        <div className="space-y-4 p-6 border border-white/10 bg-obsidian-light">
          <h3 className="font-syne font-bold text-gold mb-4">Réseaux sociaux</h3>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Facebook</label>
            <input type="text" value={settings?.social_facebook || ''} onChange={e => setSettings({...settings, social_facebook: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Instagram</label>
            <input type="text" value={settings?.social_instagram || ''} onChange={e => setSettings({...settings, social_instagram: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Twitter/X</label>
            <input type="text" value={settings?.social_twitter || ''} onChange={e => setSettings({...settings, social_twitter: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">YouTube</label>
            <input type="text" value={settings?.social_youtube || ''} onChange={e => setSettings({...settings, social_youtube: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
        </div>

        <div className="space-y-4 p-6 border border-white/10 bg-obsidian-light md:col-span-2">
          <h3 className="font-syne font-bold text-gold mb-4">SEO</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/50 mb-1 block">Meta title par défaut</label>
              <input type="text" value={settings?.default_meta_title || ''} onChange={e => setSettings({...settings, default_meta_title: e.target.value})}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
            </div>
            <div>
              <label className="text-sm text-white/50 mb-1 block">Meta description</label>
              <input type="text" value={settings?.default_meta_description || ''} onChange={e => setSettings({...settings, default_meta_description: e.target.value})}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
            </div>
          </div>
        </div>

        <div className="space-y-4 p-6 border border-white/10 bg-obsidian-light md:col-span-2">
          <h3 className="font-syne font-bold text-gold mb-4">Mentions légales</h3>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Mentions légales</label>
            <textarea value={settings?.legal_mentions || ''} onChange={e => setSettings({...settings, legal_mentions: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" rows={4} />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Politique de confidentialité</label>
            <textarea value={settings?.privacy_policy || ''} onChange={e => setSettings({...settings, privacy_policy: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" rows={4} />
          </div>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} className="bg-gold hover:bg-gold-glow text-obsidian font-bold px-6 py-3 flex items-center gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        Enregistrer
      </button>
    </div>
  );
};

// ========== USERS MODULE ==========
const UsersModule = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'editor' });

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/admin/users`, newUser);
      toast.success('Utilisateur créé');
      setCreating(false);
      setNewUser({ email: '', password: '', name: '', role: 'editor' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`);
      toast.success('Supprimé');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const isSuperAdmin = currentUser?.role === 'super_admin';

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-syne text-2xl font-bold text-white">Utilisateurs</h2>
        {isSuperAdmin && (
          <button onClick={() => setCreating(true)} className="bg-gold text-obsidian px-4 py-2 flex items-center gap-2 font-bold text-sm">
            <Plus className="w-4 h-4" /> Nouvel admin
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-4 text-sm text-white/50">Nom</th>
              <th className="text-left py-3 px-4 text-sm text-white/50">Email</th>
              <th className="text-left py-3 px-4 text-sm text-white/50">Rôle</th>
              <th className="text-left py-3 px-4 text-sm text-white/50">Date</th>
              <th className="text-right py-3 px-4 text-sm text-white/50">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-3 px-4 text-white">{u.name}</td>
                <td className="py-3 px-4 text-white/70">{u.email}</td>
                <td className="py-3 px-4">
                  <span className={`text-xs px-2 py-0.5 ${
                    u.role === 'super_admin' ? 'bg-gold/20 text-gold' :
                    u.is_admin ? 'bg-blue-500/20 text-blue-400' :
                    'bg-white/10 text-white/50'
                  }`}>
                    {u.role || (u.is_admin ? 'admin' : 'user')}
                  </span>
                </td>
                <td className="py-3 px-4 text-white/50 text-sm">{new Date(u.created_at).toLocaleDateString('fr-FR')}</td>
                <td className="py-3 px-4 text-right">
                  {isSuperAdmin && u.id !== currentUser.id && (
                    <button onClick={() => handleDelete(u.id)} className="p-2 text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-obsidian/90" onClick={() => setCreating(false)}>
          <div className="w-full max-w-md bg-obsidian-light border border-white/10 p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-syne text-xl font-bold text-white mb-6">Nouvel administrateur</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-white/50 mb-1 block">Nom</label>
                <input type="text" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})}
                  className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
              </div>
              <div>
                <label className="text-sm text-white/50 mb-1 block">Email</label>
                <input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})}
                  className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
              </div>
              <div>
                <label className="text-sm text-white/50 mb-1 block">Mot de passe</label>
                <input type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})}
                  className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
              </div>
              <div>
                <label className="text-sm text-white/50 mb-1 block">Rôle</label>
                <Select value={newUser.role} onValueChange={v => setNewUser({...newUser, role: v})}>
                  <SelectTrigger className="w-full bg-obsidian border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-obsidian-light border-white/10 z-50">
                    <SelectItem value="editor" className="text-white">Éditeur</SelectItem>
                    <SelectItem value="super_admin" className="text-gold">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setCreating(false)} className="px-4 py-2 border border-white/20 text-white">Annuler</button>
              <button onClick={handleCreate} className="px-4 py-2 bg-gold text-obsidian font-bold">Créer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== STAR SUPPORTERS MODULE ==========
const StarSupportersModule = () => {
  const [supporters, setSupporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualSupporter, setManualSupporter] = useState({
    name: '',
    display_name: '',
    email: '',
    stars: 1,
    message: '',
    is_public: true,
    is_approved: true,
  });

  const fetchSupporters = async () => {
    try {
      const res = await axios.get(`${API}/admin/supporters/stars?limit=500`);
      setSupporters(res.data || []);
    } catch (error) {
      toast.error('Erreur de chargement des soutiens');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupporters();
  }, []);

  const updateSupporter = async (id, payload) => {
    try {
      await axios.put(`${API}/admin/supporters/stars/${id}`, payload);
      fetchSupporters();
    } catch (error) {
      toast.error('Mise à jour impossible');
    }
  };

  const removeSupporter = async (id) => {
    if (!window.confirm('Supprimer ce soutien ?')) return;
    try {
      await axios.delete(`${API}/admin/supporters/stars/${id}`);
      toast.success('Soutien supprimé');
      fetchSupporters();
    } catch (error) {
      toast.error('Suppression impossible');
    }
  };

  const createManualSupporter = async () => {
    const name = manualSupporter.name.trim();
    if (name.length < 2) {
      toast.error('Nom trop court');
      return;
    }

    try {
      await axios.post(`${API}/admin/supporters/stars`, {
        name,
        display_name: manualSupporter.display_name.trim() || name,
        email: manualSupporter.email.trim() || null,
        stars: Number(manualSupporter.stars) || 1,
        message: manualSupporter.message,
        source: 'admin-manual',
        is_public: manualSupporter.is_public,
        is_approved: manualSupporter.is_approved,
      });

      toast.success('Donateur ajoute');
      setManualSupporter({
        name: '',
        display_name: '',
        email: '',
        stars: 1,
        message: '',
        is_public: true,
        is_approved: true,
      });
      fetchSupporters();
    } catch (error) {
      toast.error('Creation impossible');
    }
  };

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-syne text-2xl font-bold text-white">Soutiens étoiles</h2>
          <p className="text-sm text-white/50">Validez les noms avant affichage dans le ciel de la page d'accueil.</p>
        </div>
        <button onClick={fetchSupporters} className="p-2 border border-white/10 hover:border-gold/50 text-white/60 hover:text-gold">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 border border-white/10 bg-obsidian-light space-y-3">
        <h3 className="font-syne text-lg font-bold text-white">Ajouter un donateur manuellement</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <input
            type="text"
            placeholder="Nom interne (obligatoire)"
            value={manualSupporter.name}
            onChange={(e) => setManualSupporter({ ...manualSupporter, name: e.target.value })}
            className="px-3 py-2 bg-obsidian border border-white/10 text-white"
          />
          <input
            type="text"
            placeholder="Nom affiche (optionnel)"
            value={manualSupporter.display_name}
            onChange={(e) => setManualSupporter({ ...manualSupporter, display_name: e.target.value })}
            className="px-3 py-2 bg-obsidian border border-white/10 text-white"
          />
          <input
            type="email"
            placeholder="Email (optionnel)"
            value={manualSupporter.email}
            onChange={(e) => setManualSupporter({ ...manualSupporter, email: e.target.value })}
            className="px-3 py-2 bg-obsidian border border-white/10 text-white"
          />
          <input
            type="number"
            min="1"
            max="100"
            placeholder="Etoiles"
            value={manualSupporter.stars}
            onChange={(e) => setManualSupporter({ ...manualSupporter, stars: e.target.value })}
            className="px-3 py-2 bg-obsidian border border-white/10 text-white"
          />
          <input
            type="text"
            placeholder="Message (optionnel)"
            value={manualSupporter.message}
            onChange={(e) => setManualSupporter({ ...manualSupporter, message: e.target.value })}
            className="px-3 py-2 bg-obsidian border border-white/10 text-white md:col-span-2"
          />
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-white/80">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={manualSupporter.is_public}
              onChange={(e) => setManualSupporter({ ...manualSupporter, is_public: e.target.checked })}
            />
            Public
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={manualSupporter.is_approved}
              onChange={(e) => setManualSupporter({ ...manualSupporter, is_approved: e.target.checked })}
            />
            Approuve
          </label>
          <button
            onClick={createManualSupporter}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-obsidian font-bold hover:bg-gold-glow"
          >
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {supporters.map((supporter) => (
          <div key={supporter.id} className="p-4 border border-white/10 bg-obsidian-light">
            <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-white">{supporter.display_name || supporter.name || 'Sans nom'}</span>
                  <span className="text-xs px-2 py-0.5 bg-gold/20 text-gold">{supporter.stars || 1} etoile(s)</span>
                  {supporter.is_approved ? (
                    <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400">Approuve</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-300">En attente</span>
                  )}
                  {supporter.is_public ? (
                    <span className="text-xs px-2 py-0.5 bg-electric-blue/20 text-electric-cyan">Public</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 bg-white/10 text-white/50">Prive</span>
                  )}
                </div>
                <p className="text-xs text-white/50">
                  {supporter.email || 'Sans email'} • {supporter.source || 'chatbot'} • {new Date(supporter.created_at).toLocaleString('fr-FR')}
                </p>
                {supporter.message && <p className="text-sm text-white/70">"{supporter.message}"</p>}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => updateSupporter(supporter.id, { is_approved: !supporter.is_approved })}
                  className={`p-2 border ${supporter.is_approved ? 'border-green-500/30 text-green-400' : 'border-white/20 text-white/60'} hover:bg-white/5`}
                  title="Valider / dévalider"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => updateSupporter(supporter.id, { is_public: !supporter.is_public })}
                  className="p-2 border border-white/20 text-white/60 hover:bg-white/5"
                  title="Afficher / masquer publiquement"
                >
                  {supporter.is_public ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => {
                    const displayName = window.prompt('Nom affiche', supporter.display_name || supporter.name || '');
                    if (displayName !== null) {
                      updateSupporter(supporter.id, { display_name: displayName });
                    }
                  }}
                  className="p-2 border border-white/20 text-white/60 hover:bg-white/5"
                  title="Modifier le nom affiché"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeSupporter(supporter.id)}
                  className="p-2 border border-red-500/30 text-red-400 hover:bg-red-500/10"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {supporters.length === 0 && (
        <div className="p-8 text-center border border-white/10 bg-obsidian-light text-white/50">
          Aucun soutien enregistré pour le moment.
        </div>
      )}
    </div>
  );
};

// ========== AUDIT LOGS MODULE ==========
const AuditLogsModule = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await axios.get(`${API}/admin/audit-logs?limit=200`);
        setLogs(res.data);
      } catch (error) {
        toast.error('Erreur');
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      <h2 className="font-syne text-2xl font-bold text-white">Journal d'audit</h2>

      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {logs.map(log => (
          <div key={log.id} className="p-3 border border-white/10 bg-obsidian-light flex items-center justify-between">
            <div>
              <span className="text-gold font-medium">{log.action}</span>
              <span className="text-white/50 ml-2">sur {log.target_type}</span>
              {log.details && <span className="text-white/30 ml-2">({log.details})</span>}
              <p className="text-xs text-white/40">par {log.user_email}</p>
            </div>
            <span className="text-xs text-white/30">{new Date(log.created_at).toLocaleString('fr-FR')}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ========== PAGE VISIBILITY MODULE ==========
const PageVisibilityModule = () => {
  const [pagesStatus, setPagesStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const pagesList = [
    { key: 'proposer', label: 'Proposer', description: 'Page pour soumettre des propositions' },
    { key: 'voter', label: 'Voter', description: 'Page de vote' },
    { key: 'gala', label: 'Le Gala', description: 'Événement annuel' },
    { key: 'resultats', label: 'Résultats', description: 'Palmarès et gagnants' },
    { key: 'categories', label: 'Catégories', description: 'Liste des catégories' },
    { key: 'actualites', label: 'Actualités', description: 'Dernières actualités' },
    { key: 'partenaires', label: 'Partenaires', description: 'Nos partenaires' },
  ];

  const fetchPagesStatus = async () => {
    try {
      const res = await axios.get(`${API}/pages/status`);
      setPagesStatus(res.data);
    } catch (error) {
      toast.error('Erreur de chargement du statut des pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPagesStatus(); }, []);

  const handleToggleVisibility = async (pageKey) => {
    if (!pagesStatus) return;
    
    setSaving(prev => ({ ...prev, [pageKey]: true }));
    try {
      const newStatus = { ...pagesStatus, [pageKey]: !pagesStatus[pageKey] };
      await axios.put(`${API}/admin/pages/status`, newStatus);
      setPagesStatus(newStatus);
      toast.success(newStatus[pageKey] ? 'Page activée' : 'Page désactivée');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(prev => ({ ...prev, [pageKey]: false }));
    }
  };

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;
  if (!pagesStatus) return <div className="text-white">Impossible de charger le statut des pages</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-syne text-2xl font-bold text-white">Visibilité des pages</h2>
          <p className="text-white/50 text-sm mt-1">Activez ou désactivez l'accès public aux pages du site</p>
        </div>
        <RefreshCw className="w-6 h-6 text-gold/50" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {pagesList.map(({ key, label, description }) => (
          <div 
            key={key} 
            className={`p-6 border transition-all ${
              pagesStatus[key]
                ? 'border-gold/30 bg-gold/5' 
                : 'border-white/10 bg-obsidian-light'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-bold text-white mb-1">{label}</h3>
                <p className="text-sm text-white/50">{description}</p>
              </div>
              <button
                onClick={() => handleToggleVisibility(key)}
                disabled={saving[key]}
                className={`ml-4 flex-shrink-0 transition-all ${
                  pagesStatus[key]
                    ? 'text-gold hover:text-gold-glow'
                    : 'text-white/30 hover:text-white/50'
                }`}
              >
                {saving[key] ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : pagesStatus[key] ? (
                  <Eye className="w-6 h-6" />
                ) : (
                  <EyeOff className="w-6 h-6" />
                )}
              </button>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
              <span className={`text-xs font-semibold px-2 py-1 ${
                pagesStatus[key]
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-red-500/20 text-red-400'
              }`}>
                {pagesStatus[key] ? '✓ Publique' : '✗ Masquée'}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 border border-white/10 bg-obsidian-light rounded text-sm text-white/60">
        <p className="flex items-center gap-2 mb-2">
          <AlertCircle className="w-4 h-4 text-gold" />
          <span className="font-medium text-white">Information importante</span>
        </p>
        <p>Les pages désactivées ne seront pas accessibles aux visiteurs. Un message leur sera affiché les redirigeant vers l'accueil.</p>
      </div>
    </div>
  );
};

// ========== PAGES CMS MODULE ==========
const BLOCK_TYPES = [
  { type: 'hero', label: 'Hero Banner', icon: ImageIcon, description: 'Bannière principale avec titre et CTA' },
  { type: 'text', label: 'Texte Riche', icon: Type, description: 'Bloc de texte avec formatage' },
  { type: 'gallery', label: 'Galerie', icon: LayoutGrid, description: 'Grille d\'images' },
  { type: 'cards', label: 'Cartes', icon: LayoutGrid, description: 'Grille de cartes avec icônes' },
  { type: 'stats', label: 'Chiffres Clés', icon: TrendingUp, description: 'Compteurs animés' },
  { type: 'faq', label: 'FAQ', icon: HelpCircle, description: 'Questions fréquentes' },
  { type: 'cta', label: 'Call to Action', icon: MousePointer, description: 'Bouton d\'action' },
  { type: 'timeline', label: 'Timeline', icon: ListOrdered, description: 'Chronologie des étapes' },
  { type: 'video', label: 'Vidéo', icon: Video, description: 'Vidéo YouTube/Vimeo' },
  { type: 'quote', label: 'Citation', icon: Quote, description: 'Citation mise en avant' },
  { type: 'logos', label: 'Logos', icon: Handshake, description: 'Grille de logos partenaires' },
];

const PagesCMSModule = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/admin/pages`);
      setPages(res.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPages(); }, []);

  const handleSelectPage = (page) => {
    setSelectedPage({ ...page, sections: page.sections || [] });
    setEditMode(true);
  };

  const handleSavePage = async () => {
    if (!selectedPage) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${selectedPage.id}`, {
        title: selectedPage.title,
        meta_title: selectedPage.meta_title,
        meta_description: selectedPage.meta_description,
        og_image: selectedPage.og_image,
        sections: selectedPage.sections
      });
      toast.success('Page enregistrée');
      fetchPages();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const addBlock = (type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      title: '',
      subtitle: '',
      content: getDefaultContent(type),
      order: selectedPage.sections.length,
      is_visible: true
    };
    setSelectedPage({
      ...selectedPage,
      sections: [...selectedPage.sections, newBlock]
    });
  };

  const updateBlock = (blockId, updates) => {
    setSelectedPage({
      ...selectedPage,
      sections: selectedPage.sections.map(s => 
        s.id === blockId ? { ...s, ...updates } : s
      )
    });
  };

  const deleteBlock = (blockId) => {
    if (!window.confirm('Supprimer ce bloc ?')) return;
    setSelectedPage({
      ...selectedPage,
      sections: selectedPage.sections.filter(s => s.id !== blockId)
    });
  };

  const moveBlock = (blockId, direction) => {
    const sections = [...selectedPage.sections];
    const index = sections.findIndex(s => s.id === blockId);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === sections.length - 1)) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
    setSelectedPage({ ...selectedPage, sections });
  };

  const duplicateBlock = (block) => {
    const newBlock = { ...block, id: `block-${Date.now()}`, order: selectedPage.sections.length };
    setSelectedPage({
      ...selectedPage,
      sections: [...selectedPage.sections, newBlock]
    });
  };

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  // Page list view
  if (!editMode) {
    return (
      <div className="space-y-6">
        <h2 className="font-syne text-2xl font-bold text-white">Pages CMS</h2>
        <p className="text-white/50">Modifiez le contenu de toutes les pages du site sans toucher au code.</p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pages.map(page => (
            <div 
              key={page.id} 
              onClick={() => handleSelectPage(page)}
              className="p-4 border border-white/10 bg-obsidian-light hover:border-gold/50 cursor-pointer transition-all group"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-white group-hover:text-gold transition-colors">{page.title}</h3>
                <Edit className="w-4 h-4 text-white/30 group-hover:text-gold" />
              </div>
              <p className="text-sm text-white/50">/{page.slug}</p>
              <p className="text-xs text-white/30 mt-2">{page.sections?.length || 0} blocs</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Page editor view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setEditMode(false)} 
            className="p-2 border border-white/10 hover:border-white/30 text-white/50 hover:text-white"
          >
            <ChevronRight className="w-5 h-5 rotate-180" />
          </button>
          <div>
            <h2 className="font-syne text-2xl font-bold text-white">{selectedPage.title}</h2>
            <p className="text-sm text-white/50">/{selectedPage.slug}</p>
          </div>
        </div>
        <button
          onClick={handleSavePage}
          disabled={saving}
          className="bg-gold hover:bg-gold-glow text-obsidian font-bold px-6 py-2 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>

      {/* SEO Settings */}
      <div className="p-4 border border-white/10 bg-obsidian-light">
        <h3 className="font-bold text-white mb-4 flex items-center gap-2">
          <Search className="w-4 h-4 text-gold" /> SEO & Meta
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-white/50 mb-1 block">Meta Title</label>
            <input
              type="text"
              value={selectedPage.meta_title || ''}
              onChange={e => setSelectedPage({...selectedPage, meta_title: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
              placeholder="Titre pour les moteurs de recherche"
            />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Image OpenGraph</label>
            <input
              type="text"
              value={selectedPage.og_image || ''}
              onChange={e => setSelectedPage({...selectedPage, og_image: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
              placeholder="URL de l'image de partage"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-white/50 mb-1 block">Meta Description</label>
            <textarea
              value={selectedPage.meta_description || ''}
              onChange={e => setSelectedPage({...selectedPage, meta_description: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
              rows={2}
              placeholder="Description pour les moteurs de recherche"
            />
          </div>
        </div>
      </div>

      {/* Add Block */}
      <div className="p-4 border border-dashed border-white/20 bg-obsidian/50">
        <h3 className="font-bold text-white mb-4 flex items-center gap-2">
          <Plus className="w-4 h-4 text-gold" /> Ajouter un bloc
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {BLOCK_TYPES.map(({ type, label, icon: Icon }) => (
            <button
              key={type}
              onClick={() => addBlock(type)}
              className="p-3 border border-white/10 hover:border-gold/50 bg-obsidian text-center transition-all group"
            >
              <Icon className="w-5 h-5 mx-auto mb-1 text-white/50 group-hover:text-gold" />
              <span className="text-xs text-white/70 group-hover:text-white">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Blocks List */}
      <div className="space-y-4">
        <h3 className="font-bold text-white">Contenu de la page ({selectedPage.sections.length} blocs)</h3>
        
        {selectedPage.sections.length === 0 ? (
          <div className="p-8 border border-white/10 bg-obsidian-light text-center">
            <LayoutGrid className="w-12 h-12 mx-auto mb-4 text-white/20" />
            <p className="text-white/50">Aucun bloc. Ajoutez des blocs ci-dessus pour construire votre page.</p>
          </div>
        ) : (
          selectedPage.sections.map((block, index) => (
            <BlockEditor
              key={block.id}
              block={block}
              index={index}
              total={selectedPage.sections.length}
              onUpdate={(updates) => updateBlock(block.id, updates)}
              onDelete={() => deleteBlock(block.id)}
              onMove={(dir) => moveBlock(block.id, dir)}
              onDuplicate={() => duplicateBlock(block)}
            />
          ))
        )}
      </div>
    </div>
  );
};

const getDefaultContent = (type) => {
  switch (type) {
    case 'hero':
      return { heading: '', subheading: '', backgroundImage: '', ctaText: '', ctaLink: '' };
    case 'text':
      return { body: '', alignment: 'left' };
    case 'gallery':
      return { images: [] };
    case 'cards':
      return { items: [] };
    case 'stats':
      return { items: [] };
    case 'faq':
      return { items: [] };
    case 'cta':
      return { text: '', buttonText: '', buttonLink: '', style: 'primary' };
    case 'timeline':
      return { items: [] };
    case 'video':
      return { url: '', caption: '' };
    case 'quote':
      return { text: '', author: '', role: '' };
    case 'logos':
      return { items: [] };
    default:
      return {};
  }
};

const BlockEditor = ({ block, index, total, onUpdate, onDelete, onMove, onDuplicate }) => {
  const [expanded, setExpanded] = useState(true);
  const blockType = BLOCK_TYPES.find(b => b.type === block.type) || { label: block.type, icon: LayoutGrid };
  const Icon = blockType.icon;

  return (
    <div className={`border ${block.is_visible ? 'border-white/10' : 'border-white/5 opacity-60'} bg-obsidian-light`}>
      {/* Block Header */}
      <div className="flex items-center justify-between p-3 border-b border-white/5 bg-obsidian/50">
        <div className="flex items-center gap-3">
          <GripVertical className="w-4 h-4 text-white/20" />
          <Icon className="w-4 h-4 text-gold" />
          <span className="font-medium text-white text-sm">{blockType.label}</span>
          {block.title && <span className="text-white/40 text-sm">- {block.title}</span>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onMove('up')} disabled={index === 0} className="p-1 text-white/30 hover:text-white disabled:opacity-30">
            <ChevronUp className="w-4 h-4" />
          </button>
          <button onClick={() => onMove('down')} disabled={index === total - 1} className="p-1 text-white/30 hover:text-white disabled:opacity-30">
            <ChevronDown className="w-4 h-4" />
          </button>
          <button onClick={onDuplicate} className="p-1 text-white/30 hover:text-white">
            <Copy className="w-4 h-4" />
          </button>
          <button onClick={() => onUpdate({ is_visible: !block.is_visible })} className="p-1 text-white/30 hover:text-white">
            <Eye className={`w-4 h-4 ${block.is_visible ? '' : 'opacity-30'}`} />
          </button>
          <button onClick={() => setExpanded(!expanded)} className="p-1 text-white/30 hover:text-white">
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
          <button onClick={onDelete} className="p-1 text-red-400 hover:text-red-300">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Block Content */}
      {expanded && (
        <div className="p-4">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Titre du bloc</label>
              <input
                type="text"
                value={block.title || ''}
                onChange={e => onUpdate({ title: e.target.value })}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
                placeholder="Titre (optionnel)"
              />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Sous-titre</label>
              <input
                type="text"
                value={block.subtitle || ''}
                onChange={e => onUpdate({ subtitle: e.target.value })}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm"
                placeholder="Sous-titre (optionnel)"
              />
            </div>
          </div>

          {/* Type-specific content editor */}
          <BlockContentEditor block={block} onUpdate={onUpdate} />
        </div>
      )}
    </div>
  );
};

const BlockContentEditor = ({ block, onUpdate }) => {
  const content = block.content || {};

  const updateContent = (updates) => {
    onUpdate({ content: { ...content, ...updates } });
  };

  switch (block.type) {
    case 'hero':
      return (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-white/50 mb-1 block">Titre principal</label>
            <input type="text" value={content.heading || ''} onChange={e => updateContent({ heading: e.target.value })}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm" placeholder="Titre du hero" />
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Sous-titre</label>
            <textarea value={content.subheading || ''} onChange={e => updateContent({ subheading: e.target.value })}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm" rows={2} placeholder="Description" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Image de fond (URL)</label>
              <input type="text" value={content.backgroundImage || ''} onChange={e => updateContent({ backgroundImage: e.target.value })}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm" placeholder="https://..." />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Texte du bouton</label>
              <input type="text" value={content.ctaText || ''} onChange={e => updateContent({ ctaText: e.target.value })}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm" placeholder="Découvrir" />
            </div>
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Lien du bouton</label>
            <input type="text" value={content.ctaLink || ''} onChange={e => updateContent({ ctaLink: e.target.value })}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm" placeholder="/categories" />
          </div>
        </div>
      );

    case 'text':
      return (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-white/50 mb-1 block">Contenu (supporte le Markdown)</label>
            <textarea value={content.body || ''} onChange={e => updateContent({ body: e.target.value })}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm font-mono" rows={6} placeholder="Votre texte ici..." />
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Alignement</label>
            <Select value={content.alignment || 'left'} onValueChange={v => updateContent({ alignment: v })}>
              <SelectTrigger className="w-40 bg-obsidian border-white/10 text-white text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-obsidian-light border-white/10 z-50">
                <SelectItem value="left" className="text-white">Gauche</SelectItem>
                <SelectItem value="center" className="text-white">Centré</SelectItem>
                <SelectItem value="right" className="text-white">Droite</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case 'video':
      return (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-white/50 mb-1 block">URL de la vidéo (YouTube ou Vimeo)</label>
            <input type="text" value={content.url || ''} onChange={e => updateContent({ url: e.target.value })}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm" placeholder="https://www.youtube.com/watch?v=..." />
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Légende</label>
            <input type="text" value={content.caption || ''} onChange={e => updateContent({ caption: e.target.value })}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm" placeholder="Description de la vidéo" />
          </div>
          {content.url && (
            <div className="aspect-video bg-obsidian border border-white/10">
              <iframe
                src={getEmbedUrl(content.url)}
                className="w-full h-full"
                allowFullScreen
                title="Video preview"
              />
            </div>
          )}
        </div>
      );

    case 'quote':
      return (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-white/50 mb-1 block">Citation</label>
            <textarea value={content.text || ''} onChange={e => updateContent({ text: e.target.value })}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm italic" rows={3} placeholder="La citation..." />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Auteur</label>
              <input type="text" value={content.author || ''} onChange={e => updateContent({ author: e.target.value })}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm" placeholder="Nom" />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Fonction</label>
              <input type="text" value={content.role || ''} onChange={e => updateContent({ role: e.target.value })}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm" placeholder="Directeur artistique" />
            </div>
          </div>
        </div>
      );

    case 'cta':
      return (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-white/50 mb-1 block">Texte d'accroche</label>
            <input type="text" value={content.text || ''} onChange={e => updateContent({ text: e.target.value })}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm" placeholder="Prêt à participer ?" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Texte du bouton</label>
              <input type="text" value={content.buttonText || ''} onChange={e => updateContent({ buttonText: e.target.value })}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm" placeholder="Proposer un artiste" />
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Lien</label>
              <input type="text" value={content.buttonLink || ''} onChange={e => updateContent({ buttonLink: e.target.value })}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white text-sm" placeholder="/proposer" />
            </div>
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Style</label>
            <Select value={content.style || 'primary'} onValueChange={v => updateContent({ style: v })}>
              <SelectTrigger className="w-40 bg-obsidian border-white/10 text-white text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-obsidian-light border-white/10 z-50">
                <SelectItem value="primary" className="text-gold">Or (Primary)</SelectItem>
                <SelectItem value="secondary" className="text-electric-cyan">Bleu (Secondary)</SelectItem>
                <SelectItem value="outline" className="text-white">Outline</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

    case 'stats':
    case 'cards':
    case 'faq':
    case 'timeline':
      return <ItemsEditor type={block.type} items={content.items || []} onChange={items => updateContent({ items })} />;

    case 'gallery':
    case 'logos':
      return <ImagesEditor type={block.type} items={content.items || content.images || []} onChange={items => updateContent({ items })} />;

    default:
      return (
        <div className="p-4 border border-white/10 bg-obsidian text-center">
          <p className="text-white/50">Éditeur pour le type "{block.type}" à implémenter</p>
        </div>
      );
  }
};

const ItemsEditor = ({ type, items, onChange }) => {
  const addItem = () => {
    let newItem = { id: `item-${Date.now()}` };
    switch (type) {
      case 'stats': newItem = { ...newItem, value: '0', label: '', icon: '' }; break;
      case 'cards': newItem = { ...newItem, title: '', description: '', icon: '', link: '' }; break;
      case 'faq': newItem = { ...newItem, question: '', answer: '' }; break;
      case 'timeline': newItem = { ...newItem, title: '', description: '', date: '' }; break;
      default: break;
    }
    onChange([...items, newItem]);
  };

  const updateItem = (id, updates) => {
    onChange(items.map(item => item.id === id ? { ...item, ...updates } : item));
  };

  const removeItem = (id) => {
    onChange(items.filter(item => item.id !== id));
  };

  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div key={item.id} className="p-3 border border-white/10 bg-obsidian relative">
          <button onClick={() => removeItem(item.id)} className="absolute top-2 right-2 p-1 text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
          {type === 'stats' && (
            <div className="grid grid-cols-3 gap-2">
              <input type="text" value={item.value || ''} onChange={e => updateItem(item.id, { value: e.target.value })}
                className="px-2 py-1 bg-obsidian-light border border-white/10 text-white text-sm" placeholder="100+" />
              <input type="text" value={item.label || ''} onChange={e => updateItem(item.id, { label: e.target.value })}
                className="px-2 py-1 bg-obsidian-light border border-white/10 text-white text-sm" placeholder="Label" />
              <input type="text" value={item.icon || ''} onChange={e => updateItem(item.id, { icon: e.target.value })}
                className="px-2 py-1 bg-obsidian-light border border-white/10 text-white text-sm" placeholder="Icône" />
            </div>
          )}
          {type === 'cards' && (
            <div className="grid grid-cols-2 gap-2">
              <input type="text" value={item.title || ''} onChange={e => updateItem(item.id, { title: e.target.value })}
                className="px-2 py-1 bg-obsidian-light border border-white/10 text-white text-sm" placeholder="Titre" />
              <input type="text" value={item.link || ''} onChange={e => updateItem(item.id, { link: e.target.value })}
                className="px-2 py-1 bg-obsidian-light border border-white/10 text-white text-sm" placeholder="Lien" />
              <textarea value={item.description || ''} onChange={e => updateItem(item.id, { description: e.target.value })}
                className="col-span-2 px-2 py-1 bg-obsidian-light border border-white/10 text-white text-sm" rows={2} placeholder="Description" />
            </div>
          )}
          {type === 'faq' && (
            <div className="space-y-2">
              <input type="text" value={item.question || ''} onChange={e => updateItem(item.id, { question: e.target.value })}
                className="w-full px-2 py-1 bg-obsidian-light border border-white/10 text-white text-sm font-medium" placeholder="Question ?" />
              <textarea value={item.answer || ''} onChange={e => updateItem(item.id, { answer: e.target.value })}
                className="w-full px-2 py-1 bg-obsidian-light border border-white/10 text-white text-sm" rows={2} placeholder="Réponse..." />
            </div>
          )}
          {type === 'timeline' && (
            <div className="grid grid-cols-3 gap-2">
              <input type="text" value={item.date || ''} onChange={e => updateItem(item.id, { date: e.target.value })}
                className="px-2 py-1 bg-obsidian-light border border-white/10 text-white text-sm" placeholder="Étape 1" />
              <input type="text" value={item.title || ''} onChange={e => updateItem(item.id, { title: e.target.value })}
                className="px-2 py-1 bg-obsidian-light border border-white/10 text-white text-sm" placeholder="Titre" />
              <input type="text" value={item.description || ''} onChange={e => updateItem(item.id, { description: e.target.value })}
                className="px-2 py-1 bg-obsidian-light border border-white/10 text-white text-sm" placeholder="Description" />
            </div>
          )}
        </div>
      ))}
      <button onClick={addItem} className="w-full py-2 border border-dashed border-white/20 text-white/50 hover:text-white hover:border-white/40 text-sm">
        <Plus className="w-4 h-4 inline mr-1" /> Ajouter un élément
      </button>
    </div>
  );
};

const ImagesEditor = ({ type, items, onChange }) => {
  const addImage = () => {
    onChange([...items, { id: `img-${Date.now()}`, url: '', alt: '', link: '' }]);
  };

  const updateImage = (id, updates) => {
    onChange(items.map(item => item.id === id ? { ...item, ...updates } : item));
  };

  const removeImage = (id) => {
    onChange(items.filter(item => item.id !== id));
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map(item => (
          <div key={item.id} className="relative group">
            <div className="aspect-square bg-obsidian border border-white/10 overflow-hidden">
              {item.url ? (
                <img src={item.url} alt={item.alt || ''} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/20">
                  <ImageIcon className="w-8 h-8" />
                </div>
              )}
            </div>
            <div className="absolute inset-0 bg-obsidian/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button onClick={() => removeImage(item.id)} className="p-2 bg-red-500/20 text-red-400">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <input type="text" value={item.url || ''} onChange={e => updateImage(item.id, { url: e.target.value })}
              className="w-full mt-1 px-2 py-1 bg-obsidian border border-white/10 text-white text-xs" placeholder="URL image" />
          </div>
        ))}
        <button onClick={addImage} className="aspect-square border border-dashed border-white/20 flex items-center justify-center text-white/30 hover:text-white hover:border-white/40">
          <Plus className="w-8 h-8" />
        </button>
      </div>
    </div>
  );
};

const getEmbedUrl = (url) => {
  if (!url) return '';
  const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
};

// ========== RESULTS MODULE ==========
const ResultsModule = () => {
  const [results, setResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchData = async () => {
    try {
      const [resRes, catRes, propRes] = await Promise.all([
        axios.get(`${API}/admin/results`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/proposals?finalists_only=true`)
      ]);
      setResults(resRes.data);
      setCategories(catRes.data);
      setProposals(propRes.data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (result) => {
    try {
      if (result.id) {
        await axios.put(`${API}/admin/results/${result.id}`, result);
        toast.success('Résultat mis à jour');
      } else {
        await axios.post(`${API}/admin/results`, result);
        toast.success('Résultat créé');
      }
      setEditing(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce résultat ?')) return;
    try {
      await axios.delete(`${API}/admin/results/${id}`);
      toast.success('Supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handlePublish = async (id, isPublished) => {
    try {
      await axios.put(`${API}/admin/results/${id}`, { is_published: isPublished });
      toast.success(isPublished ? 'Publié' : 'Dépublié');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || id;
  const getProposalName = (id) => proposals.find(p => p.id === id)?.nominee_name || id;

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-syne text-2xl font-bold text-white">Résultats & Palmarès</h2>
        <button
          onClick={() => setEditing({ category_id: '', winner_proposal_id: '', edition: '2026', is_published: false })}
          className="bg-gold text-obsidian px-4 py-2 flex items-center gap-2 font-bold text-sm"
        >
          <Plus className="w-4 h-4" /> Nouveau résultat
        </button>
      </div>

      {categories.map(cat => {
        const catResult = results.find(r => r.category_id === cat.id);
        return (
          <div key={cat.id} className="p-4 border border-white/10 bg-obsidian-light">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white">{cat.name}</h3>
                {catResult ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Trophy className="w-4 h-4 text-gold" />
                    <span className="text-gold font-medium">{catResult.winner?.nominee_name || getProposalName(catResult.winner_proposal_id)}</span>
                    <span className={`text-xs px-2 py-0.5 ${catResult.is_published ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                      {catResult.is_published ? 'Publié' : 'Brouillon'}
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-white/40 mt-1">Aucun gagnant défini</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {catResult ? (
                  <>
                    <button onClick={() => handlePublish(catResult.id, !catResult.is_published)}
                      className={`p-2 ${catResult.is_published ? 'text-green-400' : 'text-white/50'} hover:bg-white/5`}>
                      <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditing(catResult)} className="p-2 text-white/50 hover:text-white">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(catResult.id)} className="p-2 text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => setEditing({ category_id: cat.id, winner_proposal_id: '', edition: '2026', is_published: false })}
                    className="px-3 py-1 border border-gold/50 text-gold text-sm hover:bg-gold/10"
                  >
                    Définir le gagnant
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {editing && (
        <ResultEditModal 
          result={editing} 
          categories={categories} 
          proposals={proposals} 
          onSave={handleSave} 
          onClose={() => setEditing(null)} 
        />
      )}
    </div>
  );
};

const ResultEditModal = ({ result, categories, proposals, onSave, onClose }) => {
  const [form, setForm] = useState(result);
  const finalists = proposals.filter(p => !form.category_id || p.category_id === form.category_id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-obsidian/90" onClick={onClose}>
      <div className="w-full max-w-lg bg-obsidian-light border border-white/10 p-6" onClick={e => e.stopPropagation()}>
        <h3 className="font-syne text-xl font-bold text-white mb-6">
          {result.id ? 'Modifier le résultat' : 'Nouveau résultat'}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-white/50 mb-1 block">Catégorie</label>
            <Select value={form.category_id} onValueChange={v => setForm({...form, category_id: v, winner_proposal_id: ''})}>
              <SelectTrigger className="w-full bg-obsidian border-white/10 text-white">
                <SelectValue placeholder="Sélectionner une catégorie" />
              </SelectTrigger>
              <SelectContent className="bg-obsidian-light border-white/10 z-50">
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id} className="text-white">{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Gagnant</label>
            <Select value={form.winner_proposal_id} onValueChange={v => setForm({...form, winner_proposal_id: v})}>
              <SelectTrigger className="w-full bg-obsidian border-white/10 text-white">
                <SelectValue placeholder="Sélectionner le gagnant" />
              </SelectTrigger>
              <SelectContent className="bg-obsidian-light border-white/10 z-50">
                {finalists.map(p => (
                  <SelectItem key={p.id} value={p.id} className="text-white">{p.nominee_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Texte de présentation</label>
            <textarea value={form.presentation_text || ''} onChange={e => setForm({...form, presentation_text: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-white/50 mb-1 block">Édition</label>
              <input type="text" value={form.edition || '2026'} onChange={e => setForm({...form, edition: e.target.value})}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input type="checkbox" checked={form.is_published} onChange={e => setForm({...form, is_published: e.target.checked})} />
              <label className="text-sm text-white/70">Publier immédiatement</label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 border border-white/20 text-white hover:bg-white/5">Annuler</button>
          <button onClick={() => onSave(form)} className="px-4 py-2 bg-gold text-obsidian font-bold">Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

// ========== GALA MODULE ==========
const GalaModule = () => {
  const [gala, setGala] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchGala = async () => {
    try {
      const res = await axios.get(`${API}/gala`);
      setGala(res.data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGala(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/gala`, gala);
      toast.success('Informations du gala enregistrées');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const addProgramItem = () => {
    setGala({
      ...gala,
      program: [...(gala.program || []), { time: '', title: '', description: '' }]
    });
  };

  const updateProgramItem = (idx, updates) => {
    const program = [...(gala.program || [])];
    program[idx] = { ...program[idx], ...updates };
    setGala({ ...gala, program });
  };

  const removeProgramItem = (idx) => {
    setGala({
      ...gala,
      program: gala.program.filter((_, i) => i !== idx)
    });
  };

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-syne text-2xl font-bold text-white">Le Gala</h2>
        <button onClick={handleSave} disabled={saving} className="bg-gold hover:bg-gold-glow text-obsidian font-bold px-6 py-2 flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Info de base */}
        <div className="space-y-4 p-6 border border-white/10 bg-obsidian-light">
          <h3 className="font-syne font-bold text-gold mb-4">Informations générales</h3>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Date de l'événement</label>
            <input type="datetime-local" value={gala?.date?.slice(0, 16) || ''} onChange={e => setGala({...gala, date: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Lieu</label>
            <input type="text" value={gala?.location || ''} onChange={e => setGala({...gala, location: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" placeholder="Lannion, Bretagne" />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Adresse complète</label>
            <input type="text" value={gala?.address || ''} onChange={e => setGala({...gala, address: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" placeholder="Salle des fêtes, 123 rue..." />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Lien billetterie</label>
            <input type="text" value={gala?.ticket_url || ''} onChange={e => setGala({...gala, ticket_url: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" placeholder="https://billetterie.com/..." />
          </div>
        </div>

        {/* Description et médias */}
        <div className="space-y-4 p-6 border border-white/10 bg-obsidian-light">
          <h3 className="font-syne font-bold text-gold mb-4">Description & Médias</h3>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Description</label>
            <textarea value={gala?.description || ''} onChange={e => setGala({...gala, description: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" rows={4} placeholder="La soirée de gala..." />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Vidéo teaser (URL)</label>
            <input type="text" value={gala?.teaser_video || ''} onChange={e => setGala({...gala, teaser_video: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" placeholder="https://youtube.com/..." />
          </div>
          <div>
            <label className="text-sm text-white/50 mb-1 block">Informations pratiques</label>
            <textarea value={gala?.practical_info || ''} onChange={e => setGala({...gala, practical_info: e.target.value})}
              className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white" rows={3} placeholder="Parking, transports, dress code..." />
          </div>
        </div>
      </div>

      {/* Programme */}
      <div className="p-6 border border-white/10 bg-obsidian-light">
        <h3 className="font-syne font-bold text-gold mb-4">Programme de la soirée</h3>
        <div className="space-y-3">
          {(gala?.program || []).map((item, idx) => (
            <div key={idx} className="flex items-start gap-3 p-3 border border-white/5 bg-obsidian">
              <input type="text" value={item.time || ''} onChange={e => updateProgramItem(idx, { time: e.target.value })}
                className="w-24 px-2 py-1 bg-obsidian-light border border-white/10 text-gold text-sm" placeholder="20h00" />
              <input type="text" value={item.title || ''} onChange={e => updateProgramItem(idx, { title: e.target.value })}
                className="flex-1 px-2 py-1 bg-obsidian-light border border-white/10 text-white text-sm" placeholder="Titre" />
              <input type="text" value={item.description || ''} onChange={e => updateProgramItem(idx, { description: e.target.value })}
                className="flex-1 px-2 py-1 bg-obsidian-light border border-white/10 text-white/70 text-sm" placeholder="Description" />
              <button onClick={() => removeProgramItem(idx)} className="p-1 text-red-400">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button onClick={addProgramItem} className="w-full py-2 border border-dashed border-white/20 text-white/50 hover:text-white text-sm">
            <Plus className="w-4 h-4 inline mr-1" /> Ajouter une étape
          </button>
        </div>
      </div>
    </div>
  );
};

// ========== MEDIA LIBRARY MODULE ==========
const MediaModule = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [altText, setAltText] = useState('');
  const [filterText, setFilterText] = useState('');

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedia(res.data || []);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      toast.error('Erreur lors du chargement des médias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png', 'image/x-png', 'image/gif', 'image/webp', ''];
      const extension = (file.name.split('.').pop() || '').toLowerCase();
      const validExtensions = ['jpg', 'jpeg', 'jpe', 'jfif', 'png', 'gif', 'webp'];
      if (!validTypes.includes(file.type) && !validExtensions.includes(extension)) {
        toast.error('Type de fichier non supporté. Formats acceptés: JPEG, PNG, GIF, WebP');
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Fichier trop volumineux. Taille maximum: 50MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Sélectionnez une image à uploader');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('alt_text', altText);

      await axios.post(`${API}/admin/media/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Image uploadée avec succès');
      setSelectedFile(null);
      setAltText('');
      document.getElementById('media-upload-input').value = '';
      await fetchMedia();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/media/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image supprimée');
      await fetchMedia();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copiée');
  };

  const filteredMedia = media.filter(m =>
    m.filename.toLowerCase().includes(filterText.toLowerCase()) ||
    m.alt_text.toLowerCase().includes(filterText.toLowerCase())
  );

  if (loading) return <Loader2 className="w-8 h-8 text-gold animate-spin mx-auto" />;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-syne text-2xl font-bold text-white mb-2">Médiathèque</h2>
        <p className="text-white/50">Uploadez et gérez vos images en HD. Utilisez-les dans les articles d'actualités.</p>
      </div>

      {/* Upload Section */}
      <div className="border-2 border-dashed border-white/20 rounded-lg p-6 bg-obsidian-light">
        <h3 className="font-bold text-white mb-4">Uploader une nouvelle image</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-center gap-4">
            <input
              type="file"
              id="media-upload-input"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <label htmlFor="media-upload-input" className="cursor-pointer">
              <div className="flex items-center gap-2 px-4 py-2 bg-obsidian border border-white/20 text-white hover:bg-white/5 rounded">
                <Upload size={18} />
                <span>Choisir une image</span>
              </div>
            </label>
            
            {selectedFile && (
              <div className="flex items-center gap-2 text-sm text-white/70">
                <span className="truncate max-w-xs">{selectedFile.name}</span>
                <span className="text-white/50">({(selectedFile.size / 1024 / 1024).toFixed(2)}MB)</span>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="text-red-400 hover:text-red-500"
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          {selectedFile && (
            <>
              <input
                type="text"
                placeholder="Texte alternatif (optionnel - pour l'accessibilité)"
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                className="w-full px-3 py-2 bg-obsidian border border-white/10 text-white rounded placeholder-white/30"
              />
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="w-full px-4 py-2 bg-gold text-obsidian font-bold rounded disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Upload en cours...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    Uploader l'image
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search/Filter */}
      {media.length > 0 && (
        <div className="relative">
          <input
            type="text"
            placeholder="Rechercher dans la médiathèque..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="w-full px-4 py-2 bg-obsidian border border-white/10 text-white rounded placeholder-white/30"
          />
          <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30" />
        </div>
      )}

      {/* Media Grid */}
      {filteredMedia.length > 0 ? (
        <div>
          <p className="text-sm text-white/50 mb-3">
            {filteredMedia.length} image{filteredMedia.length > 1 ? 's' : ''} trouvée{filteredMedia.length > 1 ? 's' : ''}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredMedia.map(m => (
              <div
                key={m.id}
                className="relative group border border-white/10 rounded-lg overflow-hidden bg-obsidian hover:border-white/30 transition"
              >
                <div className="aspect-square bg-obsidian overflow-hidden">
                  <img
                    src={m.url}
                    alt={m.alt_text || m.filename}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="absolute inset-0 bg-obsidian/95 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-3">
                  <div className="flex gap-1">
                    <button
                      onClick={() => copyUrl(m.url)}
                      title="Copier l'URL"
                      className="p-2 bg-white/10 text-white hover:bg-gold hover:text-obsidian rounded transition"
                    >
                      <Copy size={16} />
                    </button>
                    <a
                      href={m.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Ouvrir l'image"
                      className="p-2 bg-white/10 text-white hover:bg-white/20 rounded transition"
                    >
                      <ExternalLink size={16} />
                    </a>
                    <button
                      onClick={() => handleDelete(m.id)}
                      title="Supprimer l'image"
                      className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/40 rounded transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-obsidian/80 text-white/70 p-2 text-xs opacity-0 group-hover:opacity-100 transition">
                  {m.width && m.height && (
                    <div className="font-semibold text-white">{m.width}x{m.height}px</div>
                  )}
                  <div className="truncate">{m.filename}</div>
                  <div>{(m.size / 1024).toFixed(1)}KB</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : media.length > 0 ? (
        <div className="text-center py-8 text-white/50">
          <p>Aucune image ne correspond à votre recherche</p>
        </div>
      ) : (
        <div className="text-center py-12 border border-white/10 bg-obsidian-light rounded">
          <ImageIcon className="w-12 h-12 mx-auto mb-4 text-white/20" />
          <p className="text-white/50">Aucune image dans la médiathèque</p>
          <p className="text-white/30 text-sm mt-2">Uploadez votre première image ci-dessus</p>
        </div>
      )}
    </div>
  );
};

// ========== PLACEHOLDER MODULES ==========
const PlaceholderModule = ({ title }) => (
  <div className="text-center py-20">
    <h2 className="font-syne text-2xl font-bold text-white mb-4">{title}</h2>
    <p className="text-white/50">Module en cours de développement</p>
  </div>
);

// ========== MAIN ADMIN COMPONENT ==========
const Admin = () => {
  const { user, isAdmin, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [phase, setPhase] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`);
      setDashboardData(res.data);
      setPhase(res.data.phase);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchDashboard();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [isAdmin, authLoading, fetchDashboard]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Wait for auth to load before redirecting
  if (authLoading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;
  
  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-obsidian pt-32 flex items-center justify-center">
        <div className="text-center">
          <Settings className="w-16 h-16 text-white/20 mx-auto mb-6" />
          <h1 className="font-syne text-2xl font-bold text-white mb-4">Accès refusé</h1>
          <p className="text-white/50">Page réservée aux administrateurs.</p>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  const renderModule = () => {
    switch (activeModule) {
      case 'dashboard':
        return <DashboardModule data={dashboardData} onRefresh={fetchDashboard} />;
      case 'phases':
        return <PhasesModule phase={phase} onUpdate={fetchDashboard} />;
      case 'categories':
        return <CategoriesModule />;
      case 'proposals':
        return <ProposalsModule />;
      case 'finalists':
        return <ProposalsModule />; // Same component, filtered
      case 'votes':
        return <VotesModule />;
      case 'results':
        return <ResultsModule />;
      case 'news':
        return <NewsModule />;
      case 'partners':
        return <PartnersModule />;
      case 'supporters':
        return <StarSupportersModule />;
      case 'gala':
        return <GalaModule />;
      case 'pages':
        return <PagesCMSModule />;
      case 'visibility':
        return <PageVisibilityModule />;
      case 'media':
        return <MediaModule />;
      case 'users':
        return <UsersModule currentUser={user} />;
      case 'settings':
        return <SettingsModule />;
      case 'logs':
        return <AuditLogsModule />;
      default:
        return <DashboardModule data={dashboardData} onRefresh={fetchDashboard} />;
    }
  };

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-page">
      <Sidebar 
        activeModule={activeModule} 
        setActiveModule={setActiveModule} 
        user={user}
        onLogout={handleLogout}
      />
      
      <main className="ml-64 min-h-screen pt-24 pb-32 p-8 relative z-10">
        {renderModule()}
      </main>
    </div>
  );
};

export default Admin;
